/**
 * CustomersPanel — wiederverwendbarer Kunden-Tab.
 *
 * Kapselt die komplette Kundenverwaltung (serverseitige Suche, „Fehlende
 * Daten"-Filter, Werkzeuge/Segment/Reaktivierung, Liste mit Bearbeiten/
 * Verschieben/Auftrag/Rechnung). Wird sowohl unter „Rechnungen & Kunden" als
 * auch in „Werkstatt-Aufträge" verwendet, damit beide identisch funktionieren.
 * Lädt seine Daten selbst — einfach `<CustomersPanel />` einbinden.
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, UserPlus, ChevronDown, ChevronRight, Wrench, Merge, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import OrgBranchSwitcher from "../org/OrgBranchSwitcher";
import { ServiceRemindersCard } from "../dealer/ServiceRemindersCard";
import { ReactivationCard } from "../dealer/ReactivationCard";
import {
    CustomerList,
    CustomerSegmentBar,
    BranchBreakdown,
    CreateCustomerModal,
    CreateInvoiceModal,
} from "../../pages/InvoicesPage";

export default function CustomersPanel() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [customers, setCustomers] = useState([]);
    const [custTotal, setCustTotal] = useState(0);
    const [searchQ, setSearchQ] = useState("");
    const [custSearchResults, setCustSearchResults] = useState(null);
    const [custSearching, setCustSearching] = useState(false);
    const [custMissing, setCustMissing] = useState("");
    const [toolsOpen, setToolsOpen] = useState(false);
    const [segmentResult, setSegmentResult] = useState(null);
    const [orgBranch, setOrgBranch] = useState(null);
    // Jul-2026 (User-Meldung) — Kunden-Doubletten bereinigen (Vorschau → Ausführen)
    const [dedupeBusy, setDedupeBusy] = useState(false);
    const runDedupe = async () => {
        setDedupeBusy(true);
        try {
            const { data: preview } = await api.post("/customers/dedupe", { dry_run: true });
            if (!preview.merged_groups) {
                toast.success("Keine Kunden-Doubletten gefunden — alles sauber!");
                return;
            }
            const ok = window.confirm(
                `${preview.merged_groups} Doubletten-Gruppen gefunden (${preview.removed} überzählige Kundensätze, ${preview.repointed} Verknüpfungen wie Rechnungen/Fahrzeuge werden umgehängt).\n\nJetzt zusammenführen? Die Originale bleiben erhalten.`);
            if (!ok) return;
            const { data: res } = await api.post("/customers/dedupe", { dry_run: false });
            toast.success(`Bereinigt: ${res.merged_groups} Gruppen zusammengeführt, ${res.removed} Doubletten entfernt, ${res.repointed} Verknüpfungen umgehängt.`);
            loadAll();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Doubletten-Bereinigung fehlgeschlagen");
        } finally {
            setDedupeBusy(false);
        }
    };
    const [newCustOpen, setNewCustOpen] = useState(false);
    const [invoiceCustomer, setInvoiceCustomer] = useState(null);
    const [createInvoiceOpen, setCreateInvoiceOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);

    const loadPage = useCallback(async (p, size) => {
        try {
            const params = new URLSearchParams({ limit: String(size), skip: String((p - 1) * size) });
            const c = await api.get(`/customers?${params.toString()}`);
            setCustomers(c.data || []);
            const total = parseInt(c.headers?.["x-total-count"] || "", 10);
            setCustTotal(Number.isNaN(total) ? (c.data || []).length : total);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
        }
    }, []);

    // Lädt die aktuell offene Seite neu (z.B. nach Bearbeiten/Anlegen).
    const loadAll = useCallback(() => loadPage(page, pageSize), [loadPage, page, pageSize]);

    useEffect(() => { if (user) loadPage(page, pageSize); }, [user, loadPage, page, pageSize, orgBranch]);

    // Debounced serverseitige Kundensuche + „Fehlende Daten"-Filter.
    useEffect(() => {
        const term = searchQ.trim();
        if (!term && !custMissing) { setCustSearchResults(null); setCustSearching(false); return; }
        setCustSearching(true);
        const handle = setTimeout(async () => {
            try {
                const p = new URLSearchParams({ limit: "500" });
                if (term) p.set("q", term);
                if (custMissing) p.set("missing", custMissing);
                const r = await api.get(`/customers?${p.toString()}`);
                setCustSearchResults(r.data || []);
            } catch { setCustSearchResults([]); }
            setCustSearching(false);
        }, 350);
        return () => clearTimeout(handle);
    }, [searchQ, orgBranch, custMissing]);

    const openInvoiceFor = useCallback((customer) => {
        setInvoiceCustomer(customer);
        setCreateInvoiceOpen(true);
    }, []);

    const listCustomers = segmentResult !== null
        ? segmentResult
        : ((searchQ.trim() || custMissing) ? (custSearchResults || []) : customers);

    return (
        <div data-testid="customers-panel">
            <div className="relative mb-4">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-emerald-600" />
                <Input
                    value={searchQ}
                    onChange={(e) => setSearchQ(e.target.value)}
                    placeholder="Universal-Suche Kunden (Name, Firma, E-Mail, Adresse, USt-IdNr…)"
                    className="pl-9 rounded-sm border-2 border-emerald-500 focus:border-emerald-600 focus-visible:ring-emerald-400 placeholder:text-emerald-700/70"
                    data-testid="customers-panel-search"
                />
            </div>
            <div className="flex justify-end mb-3">
                <Button onClick={() => setNewCustOpen(true)} size="sm" className="bg-[#111827] hover:bg-black text-white rounded-sm font-bold uppercase tracking-wider" data-testid="customers-panel-new">
                    <UserPlus className="w-4 h-4 mr-1.5" /> Neuer Kunde
                </Button>
            </div>

            {user?.branch_role === "hauptsitz" && (
                <div className="mb-4 bg-white border border-gray-200 rounded-sm px-3 py-2" data-testid="customers-panel-branch-bar">
                    <div className="flex items-center gap-2">
                        <span className="text-xs font-bold uppercase tracking-wider text-gray-500">Standort:</span>
                        <OrgBranchSwitcher user={user} value={orgBranch} onChange={(b) => { setOrgBranch(b); setPage(1); }} allowAll allowReorder />
                        <span className="text-[11px] text-gray-400">Hauptsitz sieht & bearbeitet Filial-Kunden</span>
                    </div>
                    <BranchBreakdown />
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-sm mb-4" data-testid="customer-tools">
                <button type="button" onClick={() => setToolsOpen((o) => !o)}
                    className="w-full flex items-center gap-2 p-4 text-left" data-testid="customer-tools-toggle">
                    {toolsOpen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                    <Wrench className="w-4 h-4 text-[#7C3AED]" />
                    <h3 className="font-bold text-sm">Werkzeuge & Auswertungen</h3>
                    <span className="text-[11px] text-gray-400 hidden sm:inline">— Erinnerungen (HU & Geburtstag) · Segment/Mailings · Inaktiv-Reaktivierung · Fehlende Daten</span>
                    {custMissing && <span className="ml-auto text-[10px] font-bold px-2 py-0.5 rounded-sm bg-[#111827] text-white" data-testid="customer-tools-active-filter">Filter aktiv</span>}
                </button>
                {toolsOpen && (
                    <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-4">
                        <ServiceRemindersCard />
                        <CustomerSegmentBar onResult={setSegmentResult} />
                        <ReactivationCard />
                        <div className="flex items-center gap-2 flex-wrap" data-testid="customers-missing-filter">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mr-1">Fehlende Daten:</span>
                            {[
                                { k: "", l: "Alle" },
                                { k: "email", l: "ohne E-Mail" },
                                { k: "phone", l: "ohne Telefon" },
                                { k: "vehicle", l: "ohne Fahrzeug" },
                            ].map((m) => (
                                <button key={m.k || "all"} type="button" onClick={() => setCustMissing(m.k)}
                                    className={`text-xs px-3 py-1.5 rounded-sm border font-medium ${custMissing === m.k ? "bg-[#111827] text-white border-[#111827]" : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"}`}
                                    data-testid={`customers-missing-${m.k || "all"}`}>
                                    {m.l}
                                </button>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className="mb-3 text-xs text-gray-500 flex items-center gap-3 flex-wrap" data-testid="customers-count-line">
                {segmentResult !== null ? (
                    <span>Segment: <b>{segmentResult.length}</b> Kunden</span>
                ) : (searchQ.trim() || custMissing) ? (
                    custSearching
                        ? <span>Suche im gesamten Kundenbestand…</span>
                        : <span><b>{(custSearchResults || []).length}</b> Treffer{custMissing ? ` (Filter: ${custMissing === "email" ? "ohne E-Mail" : custMissing === "phone" ? "ohne Telefon" : "ohne Fahrzeug"})` : ""}{searchQ.trim() ? ` für „${searchQ.trim()}"` : ""}</span>
                ) : (
                    <span>
                        <b>{custTotal.toLocaleString("de-DE")}</b> Kunden im Bestand
                    </span>
                )}
                <button
                    type="button"
                    onClick={runDedupe}
                    disabled={dedupeBusy}
                    className="inline-flex items-center gap-1.5 text-[11px] font-bold text-gray-500 hover:text-[#111827] border border-gray-300 hover:border-[#111827] rounded-sm px-2 py-1 transition-colors disabled:opacity-50"
                    title="Doppelte Kundensätze (gleiche E-Mail/Kundennummer + Name) finden und zusammenführen — mit Vorschau vor der Ausführung"
                    data-testid="customers-dedupe-btn"
                >
                    {dedupeBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Merge className="w-3 h-3" />}
                    Doubletten bereinigen
                </button>
            </div>

            <CustomerList
                customers={listCustomers}
                onChange={loadAll}
                onCreateInvoice={openInvoiceFor}
                onCreateOrder={(c) => navigate(`/dealer/workshop-orders?action=new&customer=${c.id}`)}
                serverPagination={
                    (segmentResult === null && !searchQ.trim() && !custMissing)
                        ? {
                            total: custTotal, page, pageSize,
                            onPage: setPage,
                            onPageSize: (s) => { setPageSize(s); setPage(1); },
                        }
                        : null
                }
            />

            {newCustOpen && (
                <CreateCustomerModal
                    onClose={() => setNewCustOpen(false)}
                    onCreated={() => { setNewCustOpen(false); loadAll(); }}
                />
            )}
            {createInvoiceOpen && (
                <CreateInvoiceModal
                    customers={invoiceCustomer && !customers.some((c) => c.id === invoiceCustomer.id) ? [invoiceCustomer, ...customers] : customers}
                    initialCustomerId={invoiceCustomer?.id || ""}
                    onClose={() => { setCreateInvoiceOpen(false); setInvoiceCustomer(null); }}
                    onCreated={() => { setCreateInvoiceOpen(false); setInvoiceCustomer(null); loadAll(); }}
                />
            )}
        </div>
    );
}
